import React, { useRef, memo } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Button,
  Chip,
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

function SortableHeader({
  children,
  sortKey,
  currentSortBy,
  currentSortOrder,
  onSort,
  cellProps = {},
}) {
  const isActive = currentSortBy === sortKey;
  const isAsc = currentSortOrder === 'asc';

  return (
    <TableCell
      {...cellProps}
      sx={{
        cursor: 'pointer',
        userSelect: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: 'background.paper',
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
        ...cellProps?.sx,
      }}
      onClick={() => onSort?.(sortKey)}
      role="button"
      aria-sort={isActive ? (isAsc ? 'ascending' : 'descending') : 'none'}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          fontWeight: isActive ? 600 : 400,
          color: isActive ? 'primary.main' : 'inherit',
        }}
      >
        <span>{children}</span>
        <Box sx={{ display: 'flex', alignItems: 'center', height: 16, minWidth: 16 }}>
          {isActive ? (
            isAsc ? (
              <ArrowUpwardIcon sx={{ fontSize: '0.85rem' }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: '0.85rem' }} />
            )
          ) : (
            <UnfoldMoreIcon sx={{ fontSize: '0.85rem', color: 'text.disabled' }} />
          )}
        </Box>
      </Box>
    </TableCell>
  );
}

function TimesheetTable({
  data,
  dateRange,
  renderCells,
  navigate,
  onHeaderHover,
  onDateClick,
  departments,
  sortBy,
  sortOrder,
  onSort,
  isSorting, // принимаем флаг (используем только для стилистики курсора)
}) {
  const tableRef = useRef(null);

  if (!data?.members?.length) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: '70vh',
        mt: 2,
        overflowX: 'auto',
        '& .day-cell:not(:last-of-type)': {
          borderRight: '1px solid rgba(224,224,224,0.8)',
        },
        // пока сортируем — делаем курсор «wait» для всей таблицы
        cursor: isSorting ? 'wait' : 'auto',
      }}
    >
      <Table stickyHeader size="small" ref={tableRef}>
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2} sx={{ backgroundColor: 'background.paper', top: 0, zIndex: 2 }}>
              ID
            </TableCell>
            <TableCell rowSpan={2} sx={{ backgroundColor: 'background.paper', top: 0, zIndex: 2 }}>
              Photo
            </TableCell>

            {/* Name: клик-сортировка */}
            <SortableHeader
              sortKey="name"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              onSort={onSort}
              cellProps={{
                rowSpan: 2,
                sx: { minWidth: 160, borderRight: '1px solid rgba(224,224,224,0.8)' },
              }}
            >
              Name
            </SortableHeader>

            {/* Department/Team: клик-сортировка */}
            <SortableHeader
              sortKey="department"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              onSort={onSort}
              cellProps={{
                rowSpan: 2,
                sx: { minWidth: 180, borderRight: '1px solid rgba(224,224,224,0.8)' },
              }}
            >
              Department/Team
            </SortableHeader>

            {dateRange.map((d, i) => (
              <TableCell
                key={`day-${i}`}
                align="center"
                className="day-cell"
                sx={{ position: 'relative', cursor: 'pointer', backgroundColor: 'background.paper' }}
                onMouseEnter={(e) =>
                  onHeaderHover &&
                  onHeaderHover(e.currentTarget.getBoundingClientRect(), { top: 0, height: 0 })
                }
                onClick={() => onDateClick && onDateClick(d)}
              >
                <Tooltip title={`Filter by ${format(d, 'EEEE, MMM d, yyyy')}`} arrow>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                    {format(d, 'EE')}
                  </Typography>
                </Tooltip>
              </TableCell>
            ))}
            <TableCell rowSpan={2} sx={{ backgroundColor: 'background.paper', top: 0, zIndex: 2 }}>
              Detail
            </TableCell>
          </TableRow>

          <TableRow sx={{ borderTop: '2px solid rgba(0,0,0,0.6)' }}>
            {dateRange.map((d, i) => (
              <TableCell
                key={`date-${i}`}
                align="center"
                className="day-cell"
                sx={{
                  borderTop: '1px solid rgba(224,224,224,0.8)',
                  borderBottom: '2px solid rgba(0,0,0,0.6)',
                  position: 'relative',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) =>
                  onHeaderHover &&
                  onHeaderHover(e.currentTarget.getBoundingClientRect(), { top: 0, height: 0 })
                }
                onClick={() => onDateClick && onDateClick(d)}
              >
                <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>
                  {format(d, 'd')}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.members.map((member) => {
            const departmentName =
              departments.find((d) => Number(d.id) === Number(member.department_id))?.name || '';
            const teamName = member.team_name || '';
            const displayName =
              (departmentName && teamName ? `${departmentName} / ${teamName}` : departmentName || teamName || '—');

            return (
              <TableRow hover key={member.employee.id} sx={{ position: 'relative' }}>
                <TableCell align="center">{member.employee.id}</TableCell>
                <TableCell align="center">
                  <Avatar
                    src={member.employee.photo || undefined}
                    sx={{ width: 32, height: 32, mx: 'auto' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.9rem',
                      fontWeight: sortBy === 'name' ? 600 : 400,
                      color: sortBy === 'name' ? 'primary.main' : 'inherit',
                    }}
                  >
                    {member.employee.first_name} {member.employee.last_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={displayName}
                    sx={{
                      borderRadius: 2,
                      bgcolor: sortBy === 'department' ? 'rgba(25,118,210,0.08)' : '#f0f0f0',
                      color: sortBy === 'department' ? 'primary.main' : 'inherit',
                      fontWeight: sortBy === 'department' ? 600 : 400,
                    }}
                  />
                </TableCell>
                {renderCells(member)}
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/admin/timesheet_detail/${member.employee.chat_id}`)}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default memo(TimesheetTable);
